import { useNavigate, useParams } from 'react-router-dom';
import { useMe } from '../hooks/useMe';
import { Spinner } from '../components/ui/Spinner';
import { Breadcrumbs } from '../components/EntityHeader';
import { AccessDenied } from './AccessDenied';
import { DocumentTypesAdmin } from './admin/DocumentTypesAdmin';
import { RoleMappingsAdmin } from './admin/RoleMappingsAdmin';
import { FolderGrantsAdmin } from './admin/FolderGrantsAdmin';
import { SyncAdmin } from './admin/SyncAdmin';

type Tab = 'types' | 'roles' | 'grants' | 'sync';

/** Tab ids double as the URL segment under /admin (so /admin/sync is linkable). */
const TABS: { id: Tab; label: string }[] = [
  { id: 'types', label: 'Document types' },
  { id: 'roles', label: 'Role mappings' },
  { id: 'grants', label: 'Folder grants' },
  { id: 'sync', label: 'Sync' },
];

const DEFAULT_TAB: Tab = 'types';

function tabFromPath(segment: string | undefined): Tab {
  const id = (segment ?? '').split('/')[0];
  return TABS.some((t) => t.id === id) ? (id as Tab) : DEFAULT_TAB;
}

export function AdminPage() {
  const { isRecordsAdmin, loading } = useMe();
  const params = useParams();
  const navigate = useNavigate();
  const tab = tabFromPath(params['*']);

  if (loading) return <Spinner label="Checking your roles" />;
  if (!isRecordsAdmin) return <AccessDenied need="Records.Admin" />;

  const select = (next: Tab) => navigate(next === DEFAULT_TAB ? '/admin' : `/admin/${next}`);

  return (
    <div className="space-y-4">
      <div>
        <Breadcrumbs items={[{ label: 'Administration' }]} />
        <h1 className="mt-2 text-4xl leading-none">Administration</h1>
      </div>
      <div role="tablist" aria-label="Admin sections" className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={tab === t.id}
            aria-controls={`admin-panel-${t.id}`}
            id={`admin-tab-${t.id}`}
            className={[
              '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
              tab === t.id ? 'border-gsl-red text-accent-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
            ].join(' ')}
            onClick={() => select(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`admin-panel-${tab}`} aria-labelledby={`admin-tab-${tab}`}>
        {tab === 'types' ? <DocumentTypesAdmin /> : tab === 'roles' ? <RoleMappingsAdmin /> : tab === 'grants' ? <FolderGrantsAdmin /> : <SyncAdmin />}
      </div>
    </div>
  );
}
